import { AnimatePresence, motion } from 'framer-motion';
import { Check, Loader2 } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';

import { PerspectiveCover } from '@/components/PerspectiveCover';
import { Poster } from '@/components/Poster';
import { RailTrack } from '@/components/RailTrack';
import { Reveal } from '@/components/Reveal';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { fetchSeasons, nextEpisodeFromSeasons, seasonsKey, useTitlePrefetch } from '@/features/show/api';
import { useUpNext } from '@/features/upnext/api';
import type { UpNextItem } from '@/features/upnext/model';
import { NewChip, UpNextProgress } from '@/features/upnext/UpNextCard';
import { useUpNextEntry } from '@/features/upnext/useUpNextEntry';
import { useLocale } from '@/i18n/locale';
import { pageVariants } from '@/lib/motion';

/** The strip's cover: 120px → 140px from `md` — compact, so the wall stays the hero. */
const COVER = 'w-[120px] md:w-[140px]';
const COVER_SIZES = '(min-width: 768px) 140px, 120px';

/**
 * The Library's Up next strip (screens.md §`/library`): every series in
 * progress — not capped like Tonight's — as a compact rail of
 * `PerspectiveCover variant="hover"` cases (Tonight's "Also in progress"
 * language, a size down). Every "✓ S2·E6" and NEW mark is neutral: nothing
 * on Library is amber.
 *
 * Renders nothing when nothing is in progress (the wall is the content) or
 * when the library failed (the wall below shows that error). Load settle:
 * the caller wraps it in a `RevealGroup`; the heading rises and the cases
 * swing in (`railItem`). A series the user just finished from the strip
 * leaves with the shared `pageVariants()` exit as soon as the optimistic
 * episode mark completes the season summaries — a failed mark restores the
 * summaries and the case.
 */
export function UpNextStrip() {
  const { t } = useLocale();
  const { items, isPending, isError } = useUpNext(Number.POSITIVE_INFINITY);
  const visible = useVisibleUpNext(items);

  if (isError || (!isPending && items.length === 0)) return null;

  const heading = t('upnext.title');

  if (isPending) {
    return (
      <section className="flex flex-col gap-3" aria-busy>
        <h2 className="text-section text-ink">{heading}</h2>
        <div className="flex gap-4 overflow-hidden">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={`${COVER} shrink-0`}>
              <Skeleton className="aspect-[2/3] w-full" />
              <Skeleton className="mt-2 h-1 w-full" />
              <Skeleton className="mt-2 h-3.5 w-2/3" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  const exit = pageVariants();

  return (
    <AnimatePresence initial={false}>
      {visible.length > 0 ? (
        <motion.section
          key="upnext-strip"
          variants={exit}
          initial="initial"
          animate="animate"
          exit="exit"
          className="flex flex-col gap-3"
          aria-labelledby="library-upnext-heading"
        >
          <Reveal>
            <h2 id="library-upnext-heading" className="text-section text-ink">
              {heading}
            </h2>
          </Reveal>
          <RailTrack
            label={heading}
            scrollLeftLabel={t('upnext.scrollLeft')}
            scrollRightLabel={t('upnext.scrollRight')}
            // Room inside the scroll box for a turned case's grown edges.
            trackClassName="-my-4 py-4"
          >
            <AnimatePresence initial={false}>
              {visible.map((item, i) => (
                // The exit wrapper is the snap item; the Reveal inside is the case's settle.
                <motion.div
                  key={item.title.id}
                  variants={exit}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="shrink-0 snap-start"
                >
                  <Reveal kind="railItem" order={i} className={COVER}>
                    <StripCase item={item} />
                  </Reveal>
                </motion.div>
              ))}
            </AnimatePresence>
          </RailTrack>
        </motion.section>
      ) : null}
    </AnimatePresence>
  );
}

/**
 * One case: the cover → `/show/:id` (no shared-element morph — the wall
 * below holds the title's canonical `poster-${id}`), the neutral ✓ quick
 * mark on its front (hover / focus, always on touch), then the progress
 * bar, the name and the next `S·E`.
 */
function StripCase({ item }: { item: UpNextItem }) {
  const { t } = useLocale();
  const entry = useUpNextEntry(item);
  const { id, name, next, progress } = entry;
  const prefetch = useTitlePrefetch(id, item.title.type);
  return (
    <article aria-label={name} className="group/cover">
      <PerspectiveCover variant="hover">
        <Link to={`/show/${id}`} {...prefetch} aria-label={name} className="block">
          <Poster
            id={id}
            name={name}
            palette={item.title.palette}
            imageUrl={item.title.posterUrl}
            imageFallbackUrl={item.title.posterFallbackUrl}
            sizes={COVER_SIZES}
            layout={false}
            lift={false}
          />
        </Link>
        {next ? (
          <div className="absolute bottom-2 left-2 z-10 opacity-0 transition-opacity duration-fast focus-within:opacity-100 group-hover/cover:opacity-100 touch:opacity-100">
            <Button
              variant="neutral"
              size="sm"
              disabled={entry.isPending}
              onClick={() => void entry.markNext()}
              aria-label={t('upnext.markWatchedA11y', { code: entry.code, title: name })}
            >
              {entry.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Check className="size-4" strokeWidth={1.75} aria-hidden />
              )}
              <span className="text-timecode">{entry.compactCode}</span>
            </Button>
          </div>
        ) : null}
      </PerspectiveCover>
      <UpNextProgress
        name={name}
        watched={progress.watched}
        total={progress.total}
        percent={entry.percent}
        className="mt-2"
      />
      <p className="mt-2 truncate text-caption font-semibold text-ink">{name}</p>
      <div className="mt-0.5 flex min-h-5 items-center gap-2 text-caption text-ink-secondary">
        {entry.nextPending ? (
          <Skeleton className="h-3 w-14" />
        ) : next ? (
          <span className="text-timecode">{entry.code}</span>
        ) : (
          <span>{t('upnext.caughtUp')}</span>
        )}
        {entry.isNew ? <NewChip tone="neutral" /> : null}
      </div>
    </article>
  );
}

/**
 * Drop a case as soon as its series is caught up — nothing after the
 * furthest watched episode (`@shared/resume`), even with skipped seasons
 * behind it. The season summaries (shared `seasonsKey` cache — the case's
 * `useNextEpisode` fetches them, the optimistic mark patches them) are
 * observed here without a second fetch (`enabled: false`); `null` = caught
 * up, `undefined` = not loaded yet. A case that was *listed* as caught up (a
 * fresh airing past the catalog total — `buildUpNext` keeps those) stays.
 */
function useVisibleUpNext(items: UpNextItem[]): UpNextItem[] {
  const resume = useQueries({
    queries: items.map((item) => ({
      queryKey: seasonsKey(item.title.id),
      queryFn: () => fetchSeasons(item.title.id),
      enabled: false,
      select: nextEpisodeFromSeasons,
    })),
  });
  const done = resume.map((q) => q.data === null);
  const doneKey = done.join(',');
  return useMemo(
    () =>
      items.filter((item, i) => {
        const listedCaughtUp = item.totalEpisodes > 0 && item.watchedCount >= item.totalEpisodes;
        return listedCaughtUp || item.isNew || !done[i];
      }),
    // `done` is rebuilt every render; its joined key is the stable dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, doneKey],
  );
}
